import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate or retrieve session ID for anonymous tracking
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

/**
 * Hook to track page views on route changes.
 * Call this once in your App component or layout.
 */
export const usePageTracking = (): void => {
  const location = useLocation();
  const previousPath = useRef<string>('');

  useEffect(() => {
    // Don't track if same page (e.g., hash changes)
    if (location.pathname === previousPath.current) {
      return;
    }
    previousPath.current = location.pathname;

    // Track page view
    const trackPageView = async () => {
      try {
        await axios.post(`${API}/analytics/pageview`, {
          page: location.pathname,
          referrer: document.referrer || null,
          session_id: getSessionId(),
        }, {
          withCredentials: true,
        });
      } catch (error) {
        // Silently fail - analytics shouldn't break the app
        console.debug('Analytics tracking failed:', error);
      }
    };

    // Small delay to ensure page has loaded
    const timeoutId = setTimeout(trackPageView, 100);

    return () => clearTimeout(timeoutId);
  }, [location.pathname]);
};

/**
 * Track a custom event (for future use)
 */
export const trackEvent = async (
  eventName: string,
  properties?: Record<string, unknown>
): Promise<void> => {
  try {
    await axios.post(`${API}/analytics/event`, {
      event: eventName,
      properties,
      session_id: getSessionId(),
    }, {
      withCredentials: true,
    });
  } catch (error) {
    console.debug('Event tracking failed:', error);
  }
};

export default usePageTracking;
